(() => {
  'use strict';
  if (customElements.get('fs-live-pairing')) return;
  const safeId = value => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const safePrice = value => Number.isSafeInteger(value) && value >= 0;
  const normalizeSize = value => {
    const size = String(value).toLowerCase().trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
    return ({ 'extra small': 'xs', 'x small': 'xs', small: 's', medium: 'm', large: 'l', 'extra large': 'xl', 'x large': 'xl', 'extra extra large': 'xxl' })[size] || size;
  };
  const allowsOne = variant => {
    const rule = variant.quantity_rule || {};
    return variant.available === true && Number(rule.min ?? 1) === 1 && Number(rule.increment ?? 1) === 1 && (rule.max == null || Number(rule.max) >= 1);
  };
  class FsLivePairing extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.pending ??= false;
      this.uncertain ??= false;
      this.cards = [...this.querySelectorAll('[data-pair-card]')];
      this.add = this.querySelector('[data-pair-add]');
      this.status = this.querySelector('[data-pair-status]');
      this.review = this.querySelector('[data-pair-review]');
      if (!this.initialized) {
        this.cards.forEach(card => {
          card.querySelector('[data-pair-include]').checked = false;
          card.querySelector('[data-pair-size]').value = '';
        });
        this.initialized = true;
      }
      this.handleChange = () => {
        if (!this.pending && !this.uncertain) {
          this.status.textContent = this.dataset.chooseLabel;
          this.review.hidden = true;
          this.refresh();
          this.sizeHints();
        }
      };
      this.handleAdd = () => this.addSelected();
      this.onProductChange = event => { if (event.target.closest?.('variant-selects')) this.sizeHints(); };
      this.addEventListener('change', this.handleChange);
      this.add.addEventListener('click', this.handleAdd);
      this.host = this.closest('product-info');
      this.host?.addEventListener('change', this.onProductChange);
      this.refresh();
      this.sizeHints();
    }
    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      this.add?.removeEventListener('click', this.handleAdd);
      this.host?.removeEventListener('change', this.onProductChange);
      this.ready = false;
    }
    selected() {
      return this.cards.filter(card => card.querySelector('[data-pair-include]').checked).map(card => {
        const select = card.querySelector('[data-pair-size]');
        const option = [...select.options].find(entry => entry.value === select.value);
        return { card, id: select.value, title: option?.dataset.title, available: option?.dataset.available === 'true' && !option.disabled, price: option?.dataset.price === undefined ? NaN : Number(option.dataset.price) };
      });
    }
    valid(selection) {
      return selection.length > 0 && selection.length <= 2 && new Set(selection.map(item => item.id)).size === selection.length && selection.every(item => safeId(item.id) && item.available && safePrice(item.price));
    }
    money(value) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency || 'PKR', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value / 100);
    }
    refresh() {
      const selection = this.selected();
      this.add.disabled = this.pending || this.uncertain || !this.valid(selection);
      this.add.textContent = this.pending ? this.dataset.pendingLabel : this.dataset.addLabel;
      this.querySelector('[data-pair-total]').textContent = this.money(selection.reduce((sum, item) => sum + (safePrice(item.price) ? item.price : 0), 0));
      this.cards.forEach(card => {
        card.classList.toggle('is-selected', Boolean(card.querySelector('[data-pair-include]').checked));
        const select = card.querySelector('[data-pair-size]');
        const option = [...select.options].find(entry => entry.value === select.value);
        const price = Number(option?.dataset.price ?? card.dataset.basePrice);
        if (safePrice(price)) card.querySelector('[data-pair-price]').textContent = this.money(price);
        card.querySelectorAll('input,select').forEach(input => { input.disabled = this.pending || this.uncertain; });
      });
      this.setAttribute('aria-busy', String(this.pending));
    }
    sizeHints() {
      if (!this.host) return;
      const size = [...this.host.querySelectorAll('variant-selects input:checked,variant-selects select')].map(input => input.value).find(value => ['xs', 's', 'm', 'l', 'xl', 'xxl'].includes(normalizeSize(value)));
      this.cards.forEach(card => {
        const select = card.querySelector('[data-pair-size]');
        const matching = [...select.options].some(option => option.dataset.available === 'true' && normalizeSize(option.dataset.title) === normalizeSize(size));
        card.querySelector('[data-pair-hint]').textContent = !select.value && size && matching ? this.dataset.sizeLabel.replace('[size]', size) : '';
      });
    }
    async request(url, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, headers: { Accept: 'application/json', ...options.headers }, signal: controller.signal });
        if (!response.ok) throw new Error('request');
        return await response.json();
      } finally { clearTimeout(timer); }
    }
    updateProduct(card, product) {
      if (String(product.id) !== card.dataset.productId || !Array.isArray(product.variants)) throw new Error('product');
      [...card.querySelector('[data-pair-size]').options].filter(option => option.value).forEach(option => {
        const variant = product.variants.find(entry => String(entry.id) === option.value);
        const available = Boolean(variant && allowsOne(variant) && product.requires_selling_plan !== true && safePrice(variant.price));
        option.disabled = !available;
        option.dataset.available = String(available);
        if (variant && safePrice(variant.price)) option.dataset.price = String(variant.price);
        if (variant?.title) option.dataset.title = variant.title;
        option.textContent = option.dataset.title + (available ? '' : ' · ' + (variant?.available === false ? this.dataset.soldOutLabel : this.dataset.viewProductLabel));
      });
      if (safePrice(product.price)) card.dataset.basePrice = String(product.price);
    }
    async addSelected() {
      if (this.pending || this.uncertain) return;
      const selection = this.selected();
      if (!this.valid(selection)) { this.refresh(); return; }
      this.pending = true;
      this.status.textContent = this.dataset.pendingLabel;
      this.review.hidden = true;
      this.refresh();
      const root = (this.dataset.root || '/').replace(/\/?$/, '/');
      let submitted = false;
      try {
        const products = await Promise.all(selection.map(item => this.request(root + 'products/' + encodeURIComponent(item.card.dataset.productHandle) + '.js')));
        selection.forEach((item, index) => this.updateProduct(item.card, products[index]));
        this.sizeHints();
        const fresh = this.selected();
        if (!this.valid(fresh)) { this.status.textContent = this.dataset.unavailableLabel; return; }
        if (selection.some((item, index) => item.price !== fresh[index].price || item.title !== fresh[index].title)) {
          this.status.textContent = this.dataset.changedLabel;
          return;
        }
        if (!this.isConnected) return;
        const cart = await this.request(root + 'cart.js');
        if (!Array.isArray(cart.items)) throw new Error('cart');
        if (selection.some(item => cart.items.some(line => String(line.variant_id) === item.id))) {
          this.status.textContent = this.dataset.existingLabel;
          this.review.hidden = false;
          return;
        }
        if (!this.isConnected) return;
        submitted = true;
        const data = await this.request(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: selection.map(item => ({ id: Number(item.id), quantity: 1 })) }) });
        const added = Array.isArray(data.items) ? data.items : [];
        if (added.length !== selection.length || selection.some(item => !added.some(line => String(line.variant_id || line.id) === item.id))) throw new Error('uncertain');
        this.uncertain = true;
        this.status.textContent = this.dataset.addedLabel;
        this.review.hidden = false;
        if (this.isConnected) window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        this.uncertain = submitted;
        this.status.textContent = submitted ? this.dataset.uncertainLabel : this.dataset.errorLabel;
        this.review.hidden = !submitted;
      } finally {
        this.pending = false;
        this.refresh();
      }
    }
  }
  customElements.define('fs-live-pairing', FsLivePairing);
})();
